import { MfDate, MfDateRange } from './MfDate';

export enum Metric {
  AD_REQUEST = 'ad-request',
  AD_REQUEST_WITH_ADS = 'ad-request-with-ads',
  ADD_TO_CART = 'add-to-cart',
  ADJUSTED_OFFLINE_ROAS = 'adjusted-offline-roas',
  ADJUSTED_ONLINE_ROAS = 'adjusted-online-roas',
  ADJUSTED_ROAS = 'adjusted-roas',
  AVG_WIN_POSITION = 'avg-win-position',
  BILLED_PRICE = 'billed-price',
  CLICK = 'click',
  CONVERSION = 'conversion',
  CPC = 'cpc',
  CPM = 'cpm',
  CPO = 'cpo',
  CTR = 'ctr',
  FILL_RATE = 'fill-rate',
  IMPRESSION = 'impression',
  OFFLINE_ROAS = 'offline-roas',
  OFFLINE_REVENUE_WITH_ADS = 'offline-revenue-with-ads',
  ONLINE_ROAS = 'online-roas',
  ONLINE_REVENUE_WITH_ADS = 'online-revenue-with-ads',
  ORDER = 'order',
  REVENUE = 'revenue',
  REVENUE_WITH_ADS = 'revenue-with-ads',
  ROAS = 'roas',
  SHOPPER = 'shopper',
  UNIT = 'unit',
  UNIT_WITH_ADS = 'unit-with-ads',
  VIDEO_COMPLETE = 'video-complete',
  VIDEO_START = 'video-start',
  WIN_RATE = 'win-rate',
}

export enum KpiType {
  AD = 'ad',
  AD_REQUEST = 'ad-request',
  ORDER = 'order',
}

export const MetricToKpiGroup = new Map<Metric, KpiType>([
  [Metric.AD_REQUEST_WITH_ADS, KpiType.AD_REQUEST],
  [Metric.AD_REQUEST, KpiType.AD_REQUEST],
  [Metric.ADD_TO_CART, KpiType.AD],
  [Metric.ADJUSTED_OFFLINE_ROAS, KpiType.AD],
  [Metric.ADJUSTED_ONLINE_ROAS, KpiType.AD],
  [Metric.ADJUSTED_ROAS, KpiType.AD],
  [Metric.AVG_WIN_POSITION, KpiType.AD],
  [Metric.BILLED_PRICE, KpiType.AD],
  [Metric.CLICK, KpiType.AD],
  [Metric.CONVERSION, KpiType.AD],
  [Metric.CPC, KpiType.AD],
  [Metric.CPM, KpiType.AD],
  [Metric.CPO, KpiType.AD],
  [Metric.CTR, KpiType.AD],
  [Metric.FILL_RATE, KpiType.AD_REQUEST],
  [Metric.IMPRESSION, KpiType.AD],
  [Metric.OFFLINE_ROAS, KpiType.AD],
  [Metric.OFFLINE_REVENUE_WITH_ADS, KpiType.AD],
  [Metric.ONLINE_ROAS, KpiType.AD],
  [Metric.ONLINE_REVENUE_WITH_ADS, KpiType.AD],
  [Metric.ORDER, KpiType.ORDER],
  [Metric.REVENUE_WITH_ADS, KpiType.AD],
  [Metric.REVENUE, KpiType.ORDER],
  [Metric.ROAS, KpiType.AD],
  [Metric.SHOPPER, KpiType.ORDER],
  [Metric.UNIT_WITH_ADS, KpiType.AD],
  [Metric.UNIT, KpiType.ORDER],
  [Metric.VIDEO_COMPLETE, KpiType.AD],
  [Metric.VIDEO_START, KpiType.AD],
  [Metric.WIN_RATE, KpiType.AD],
]);

export function getResponseFieldForMetric(metric: Metric): string | undefined {
  const responseFieldMap = new Map<Metric, string>([
    [Metric.AD_REQUEST_WITH_ADS, 'ad_request_with_ads'],
    [Metric.AD_REQUEST, 'ad_request'],
    [Metric.ADD_TO_CART, 'add_to_cart'],
    [Metric.ADJUSTED_OFFLINE_ROAS, 'adjusted_offline_roas'],
    [Metric.ADJUSTED_ONLINE_ROAS, 'adjusted_online_roas'],
    [Metric.ADJUSTED_ROAS, 'adjusted_roas'],
    [Metric.AVG_WIN_POSITION, 'avg_win_position'],
    [Metric.BILLED_PRICE, 'billed_price'],
    [Metric.CLICK, 'click'],
    [Metric.CONVERSION, 'conversion'],
    [Metric.CPC, 'cpc'],
    [Metric.CPM, 'cpm'],
    [Metric.CPO, 'cpo'],
    [Metric.CTR, 'ctr'],
    [Metric.FILL_RATE, 'fill_rate'],
    [Metric.IMPRESSION, 'impression'],
    [Metric.OFFLINE_ROAS, 'offline_roas'],
    [Metric.OFFLINE_REVENUE_WITH_ADS, 'offline_revenue_with_ads'],
    [Metric.ONLINE_ROAS, 'online_roas'],
    [Metric.ONLINE_REVENUE_WITH_ADS, 'online_revenue_with_ads'],
    [Metric.ORDER, 'order'],
    [Metric.REVENUE_WITH_ADS, 'revenue_with_ads'],
    [Metric.REVENUE, 'revenue'],
    [Metric.ROAS, 'roas'],
    [Metric.SHOPPER, 'shopper'],
    [Metric.UNIT_WITH_ADS, 'unit_with_ads'],
    [Metric.UNIT, 'unit'],
    [Metric.VIDEO_COMPLETE, 'video_complete'],
    [Metric.VIDEO_START, 'video_start'],
    [Metric.WIN_RATE, 'win_rate'],
  ]);

  return responseFieldMap.get(metric);
}

/**
 * Read a metric's value off a data point. Each KPI family has its own data point
 * shape, so the response field is not a key the union shares; anything but a
 * number counts as a missing value.
 */
export function metricValueOf(
  datum: RawTimeSeriesDataPointApiResponse,
  field: string | undefined,
): Count | undefined {
  if (!field) return undefined;
  const value: unknown = Reflect.get(datum, field);
  return typeof value === 'number' ? value : undefined;
}

export function isKPISortable(metric: Metric): boolean {
  const m = new Map<Metric, boolean>([
    [Metric.AD_REQUEST_WITH_ADS, true],
    [Metric.AD_REQUEST, true],
    [Metric.ADD_TO_CART, true],
    [Metric.ADJUSTED_OFFLINE_ROAS, true],
    [Metric.ADJUSTED_ONLINE_ROAS, true],
    [Metric.ADJUSTED_ROAS, true],
    [Metric.AVG_WIN_POSITION, true],
    [Metric.BILLED_PRICE, true],
    [Metric.CLICK, true],
    [Metric.CONVERSION, true],
    [Metric.CPC, true],
    [Metric.CPM, true],
    [Metric.CPO, true],
    [Metric.CTR, true],
    [Metric.FILL_RATE, true],
    [Metric.IMPRESSION, true],
    [Metric.OFFLINE_ROAS, true],
    [Metric.OFFLINE_REVENUE_WITH_ADS, true],
    [Metric.ONLINE_ROAS, true],
    [Metric.ONLINE_REVENUE_WITH_ADS, true],
    [Metric.ORDER, false],
    [Metric.REVENUE_WITH_ADS, true],
    [Metric.REVENUE, false],
    [Metric.ROAS, true],
    [Metric.SHOPPER, false],
    [Metric.UNIT_WITH_ADS, true],
    [Metric.UNIT, false],
    [Metric.VIDEO_COMPLETE, true],
    [Metric.VIDEO_START, true],
    [Metric.WIN_RATE, true],
  ]);

  return m.get(metric) ?? false;
}

export enum Period {
  YEAR = 'year',
  QUARTER = 'quarter',
  MONTH = 'month',
  WEEK = 'week',
  DAY = 'day',
  HOUR = 'hour',
}

export type Count = number;

export interface RawDataPoint {
  start_time: string;
  end_time: string;
  value: Count | null;
}

export interface RawAdKpisDataPoint {
  start_time: string;
  end_time: string;
  impression: number | null;
  click: number | null;
  add_to_cart: number | null;
  ctr: number | null;
  revenue_with_ads: number | null;
  online_revenue_with_ads: number | null;
  offline_revenue_with_ads: number | null;
  unit_with_ads: number | null;
  roas: number | null;
  adjusted_roas: number | null;
  offline_roas: number | null;
  adjusted_offline_roas: number | null;
  online_roas: number | null;
  adjusted_online_roas: number | null;
  billed_price: number | null;
  conversion: number | null;
  cpo: number | null;
  win_rate: number | null;
  avg_win_position: number | null;
  video_start?: number | null;
  video_complete?: number | null;
}

export interface RawAdRequestKpisDataPoint {
  start_time: string;
  end_time: string;
  ad_request: number | null;
  ad_request_with_ads: number | null;
  fill_rate: number | null;
}

export interface RawOrderKpisDataPoint {
  start_time: string;
  end_time: string;
  revenue: number | null;
  order: number | null;
  shopper: number | null;
  unit: number | null;
}

export type RawTimeSeriesDataPointApiResponse =
  | RawAdKpisDataPoint
  | RawAdRequestKpisDataPoint
  | RawDataPoint
  | RawOrderKpisDataPoint;

export interface RawSimpleReportDataApiResponse {
  event_type: Metric;
  data: RawTimeSeriesDataPointApiResponse[];
}

export interface GroupReportData {
  id: string;
  parent: string;
  name: string;
  key?: string;
  advertiser_name?: string;
  total_budget?: number | null;
  sku?: string | null;
  campaign_id?: string;
  campaign_name?: string;
  data: Array<
    | RawAdKpisDataPoint
    | RawAdRequestKpisDataPoint
    | RawOrderKpisDataPoint
    | RawTimeSeriesDataPointApiResponse
  >;
}

export enum GroupBy {
  CAMPAIGN = 'campaign',
  LINE_ITEM = 'line-item',
  PLACEMENT = 'placement',
  ADVERTISER = 'advertiser',
  CATEGORY = 'category',
  KEYWORD = 'keyword',
  PRODUCT = 'product',
  DEVICE = 'device',
}

export interface RawGroupReportDataApiResponse {
  event_type: Metric;
  group: GroupBy;
  totals?: RawTimeSeriesDataPointApiResponse | null;
  data: GroupReportData[];
}

export function getPreviousPeriodRange(endDate: Date, period: Period) {
  const startDate = new Date(endDate);
  switch (period) {
    case Period.YEAR:
      startDate.setFullYear(startDate.getFullYear() - 1);
      break;
    case Period.MONTH:
      startDate.setMonth(startDate.getMonth() - 1);
      break;
    case Period.WEEK:
      startDate.setDate(startDate.getDate() - 7);
      break;
    case Period.DAY:
      startDate.setDate(startDate.getDate() - 1);
      break;
    case Period.HOUR:
      startDate.setHours(startDate.getHours() - 1);
      break;
    default:
      throw new Error(`${period as string} not implemented`);
  }

  return {
    startDate,
    endDate,
  };
}

export function getPreviousPeriodRangeMf(endDate: MfDate, period: Period) {
  switch (period) {
    case Period.YEAR:
      return new MfDateRange(endDate.addYears(-1), endDate);
    case Period.MONTH:
      return new MfDateRange(endDate.addMonths(-1), endDate);
    case Period.WEEK:
      return new MfDateRange(endDate.addDays(-7), endDate);
    case Period.DAY:
      return new MfDateRange(endDate.addDays(-1), endDate);
    case Period.HOUR:
      return new MfDateRange(endDate.addHours(-1), endDate);
    default:
      throw new Error(`${period as string} not implemented`);
  }
}

export function getPrevMfRange(range: MfDateRange) {
  const seconds = range.end.lessDateInSeconds(range.start);

  return new MfDateRange(range.start.subtract(seconds), range.start);
}

export function addMfPeriod(date: MfDate, period: Period, count: number) {
  switch (period) {
    case Period.YEAR:
      return date.clone().addYears(count);
    case Period.MONTH:
      return date.clone().addMonths(count);
    case Period.WEEK:
      return date.clone().addDays(count * 7);
    case Period.DAY:
      return date.clone().addDays(count);
    case Period.HOUR:
      return date.clone().addHours(count);
    default:
      throw new Error(`Unsupported period ${period}`);
  }
}

export function buildDefaultDateRangeForPeriod(period: Period) {
  switch (period) {
    case Period.YEAR:
      return getPreviousPeriodRangeMf(MfDate.today().truncateDay(), Period.YEAR);
    case Period.MONTH:
      return getPreviousPeriodRangeMf(MfDate.today().truncateDay(), Period.MONTH);
    case Period.WEEK:
      return getPreviousPeriodRangeMf(MfDate.today().truncateDay(), Period.MONTH);
    case Period.DAY:
      return getPreviousPeriodRangeMf(MfDate.today().truncateDay(), Period.WEEK);
    case Period.HOUR:
      return buildDefaultDateRangeForHourlyPeriod();
    default:
      throw new Error(`${period as string} not implemented`);
  }
}

export function buildDefaultDateRangeForHourlyPeriod() {
  const now = MfDate.now();

  const range = getPreviousPeriodRangeMf(now.truncateDay(), Period.DAY);

  // Shift range so that it starts at "yesterday's" local midnight
  const localDateRange = addTimezoneOffsetToMfRange(range);
  localDateRange.end = now.truncateHour().addHours(1);

  return localDateRange;
}

export function addTimezoneOffsetToMfRange(range: MfDateRange) {
  const timezoneOffset = new Date().getTimezoneOffset();

  const res = range.clone();
  res.start = res.start.addHours(timezoneOffset / 60);
  res.end = res.end.addHours(timezoneOffset / 60);

  return res;
}

export function removeTimezoneOffsetToMfRange(range: MfDateRange) {
  const timezoneOffset = new Date().getTimezoneOffset();

  const res = range.clone();
  res.start = res.start.addHours(-timezoneOffset / 60);
  res.end = res.end.addHours(-timezoneOffset / 60);

  return res;
}
